import { Fragment } from "react";
import type { ReactNode } from "react";
import { NavLink } from "react-router-dom";
import { cx } from "./cx";

export interface AppShellNavItem {
  to: string;
  label: string;
  /** Icon content for the `.nav-icon` box; defaults to initials from the label (POC style: "PR", "SP"). */
  icon?: ReactNode;
}

export interface AppShellNavSection {
  /** Section label above the items; empty string renders no label. */
  section: string;
  items: AppShellNavItem[];
}

export interface AppShellProps {
  nav: AppShellNavSection[];
  /** Topbar title. Strings are wrapped in `<strong>` per the POC's topbar markup. */
  title: ReactNode;
  /** Right-hand topbar actions. */
  actions?: ReactNode;
  user?: { name: string } | null;
  onLogout?: () => void;
  /** Renders a "Password" button next to Log out (change-own-password flow). */
  onChangePassword?: () => void;
  children: ReactNode;
}

function navInitials(label: string): string {
  const parts = label.split(/\s+/).filter(Boolean);
  const initials =
    parts.length >= 2 ? parts.map((part) => part.charAt(0)).join("").slice(0, 2) : label.slice(0, 2);
  return initials.toUpperCase();
}

export function AppShell({
  nav,
  title,
  actions,
  user,
  onLogout,
  onChangePassword,
  children,
}: AppShellProps) {
  return (
    <div className="app">
      <aside className="sidebar">
        <div className="brand">
          <div className="brand-mark">Reina</div>
          <div>
            <h1>Reina Group</h1>
            <span>price comparison</span>
          </div>
        </div>
        <nav className="nav">
          {nav.map((group, groupIndex) => (
            <Fragment key={group.section || groupIndex}>
              {group.section ? <div className="nav-section">{group.section}</div> : null}
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) => cx(isActive && "active")}
                >
                  <span className="nav-icon">{item.icon ?? navInitials(item.label)}</span>
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </Fragment>
          ))}
        </nav>
      </aside>
      <section className="main-shell">
        <header className="topbar">
          <div className="topbar-title">{typeof title === "string" ? <strong>{title}</strong> : title}</div>
          <div className="actions">
            {actions}
            {user ? <span className="pill">{user.name}</span> : null}
            {user && onChangePassword ? (
              <button className="mini" type="button" onClick={onChangePassword}>
                Password
              </button>
            ) : null}
            {user && onLogout ? (
              <button className="mini" type="button" onClick={onLogout}>
                Log out
              </button>
            ) : null}
          </div>
        </header>
        <main className="content">{children}</main>
      </section>
    </div>
  );
}
